// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Text diff view using react-diff-view.
 * Shows unified diff between two text versions.
 */
import { useMemo, type FC } from 'react'
import { Diff, Hunk, parseDiff } from 'react-diff-view'
import { createTwoFilesPatch } from 'diff'
import 'react-diff-view/style/index.css'
import { Loader2 } from 'lucide-react'

/**
 * Convert unified diff to git-style diff format.
 * react-diff-view's parseDiff expects git-style headers (diff --git a/... b/...).
 * The `diff` library's createTwoFilesPatch produces standard unified diff which
 * parseDiff cannot parse correctly.
 */
export function createGitStyleDiff(
  oldText: string,
  newText: string,
  oldVersion: number,
  newVersion: number
): string {
  const unifiedDiff = createTwoFilesPatch(
    `v${oldVersion}`,
    `v${newVersion}`,
    oldText,
    newText,
    '', // old header
    '', // new header
    { context: 3 }
  )
  // Prepend git-style header for react-diff-view compatibility
  return `diff --git a/v${oldVersion} b/v${newVersion}\n${unifiedDiff}`
}

interface TextDiffViewProps {
  /** Old version text */
  oldText: string
  /** New version text */
  newText: string
  /** Old version number (for header) */
  oldVersion: number
  /** New version number (for header) */
  newVersion: number
  /** Loading state */
  loading?: boolean
  /** Error message */
  error?: string | null
}

export const TextDiffView: FC<TextDiffViewProps> = ({
  oldText,
  newText,
  oldVersion,
  newVersion,
  loading,
  error,
}) => {
  // Compute unified diff between the two texts
  const files = useMemo(() => {
    if (!oldText && !newText) return []

    const gitStyleDiff = createGitStyleDiff(oldText, newText, oldVersion, newVersion)

    try {
      return parseDiff(gitStyleDiff)
    } catch {
      return []
    }
  }, [oldText, newText, oldVersion, newVersion])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading text...
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-red-600">
        {error}
      </div>
    )
  }

  if (files.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500">
        No differences found between versions
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto bg-white">
      {files.map((file, i) => (
        <Diff
          key={i}
          viewType="unified"
          diffType={file.type}
          hunks={file.hunks}
          className="text-sm"
        >
          {(hunks) =>
            hunks.map((hunk) => (
              <Hunk key={hunk.content} hunk={hunk} />
            ))
          }
        </Diff>
      ))}
    </div>
  )
}
